import * as React from "react";
import { useHistory } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";

import type { AgeingBucket, OutstandingClientRow } from "api/billing";
import { isConnected } from "Components/Tenants";
import {
  Button,
  DataTable,
  EmptyState,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  Skeleton,
  Switch,
} from "Components/ui";
import { CircleCheck } from "Components/ui/icons";
import { cn } from "utils/cn";
import { downloadCsv, type CsvColumn } from "utils/download";
import { formatDate, formatINR, pluralize, todayIST } from "utils/format";
import { useBillingAnalyticsQuery } from "../data";
import { presetRange } from "../reportRanges";
import {
  CLIENT_CSV_COLUMNS,
  csvRupees,
  focusDrawer,
  ReportClient,
  ReportError,
  ReportSummary,
  ReportTableHeading,
} from "./ReportParts";

/** Ageing buckets, from not yet due to longest overdue; colours run from calm to urgent. */
const AGEING: Array<{ key: AgeingBucket; label: string; swatch: string }> = [
  { key: "notDue", label: "Not yet due", swatch: "bg-success" },
  { key: "d1_30", label: "1–30 days late", swatch: "bg-warning/50" },
  { key: "d31_60", label: "31–60 days late", swatch: "bg-warning" },
  { key: "d61_90", label: "61–90 days late", swatch: "bg-danger/60" },
  { key: "d90Plus", label: "Over 90 days late", swatch: "bg-danger" },
];

/** One bar split by how late the money is, with each bucket's amount underneath. */
const AgeingBar: React.FC<{ buckets: Record<AgeingBucket, number>; total: number }> = ({
  buckets,
  total,
}) => {
  const summary = `Outstanding by age: ${AGEING.map(
    (bucket) => `${bucket.label} ${formatINR(buckets[bucket.key])}`
  ).join(", ")}.`;
  return (
    <div className="space-y-3">
      <div
        role="img"
        aria-label={summary}
        className="flex h-3 gap-0.5 overflow-hidden rounded-full bg-surface-muted"
      >
        {AGEING.filter((bucket) => buckets[bucket.key] > 0).map((bucket) => (
          <div
            key={bucket.key}
            className={cn("h-full first:rounded-l-full last:rounded-r-full", bucket.swatch)}
            style={{ width: `${(buckets[bucket.key] / total) * 100}%` }}
          />
        ))}
      </div>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-5" aria-hidden="true">
        {AGEING.map((bucket) => (
          <li key={bucket.key} className="min-w-0">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={cn("h-2 w-2 shrink-0 rounded-full", bucket.swatch)} />
              <span className="truncate">{bucket.label}</span>
            </span>
            <span
              className={cn(
                "mt-0.5 block text-sm font-medium tabular-nums",
                buckets[bucket.key] > 0 ? "text-foreground" : "text-subtle-foreground"
              )}
            >
              {formatINR(buckets[bucket.key])}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const columns: ColumnDef<OutstandingClientRow>[] = [
  {
    id: "client",
    header: "Client",
    cell: ({ row }) => <ReportClient client={row.original} />,
  },
  {
    id: "openInvoices",
    header: "Open invoices",
    meta: { align: "right" },
    cell: ({ row }) => <span className="tabular-nums">{row.original.openInvoices}</span>,
  },
  {
    id: "outstanding",
    header: "Outstanding",
    meta: { align: "right", className: "whitespace-nowrap" },
    cell: ({ row }) => (
      <span className="font-semibold tabular-nums text-foreground">
        {formatINR(row.original.outstanding)}
      </span>
    ),
  },
  {
    id: "overdue",
    header: "Overdue",
    meta: { align: "right", className: "whitespace-nowrap" },
    cell: ({ row }) =>
      row.original.overdue > 0 ? (
        <span className="font-medium tabular-nums text-danger-text">
          {formatINR(row.original.overdue)}
        </span>
      ) : (
        <span className="text-subtle-foreground">—</span>
      ),
  },
  {
    id: "oldestDue",
    header: "Oldest due",
    meta: { className: "whitespace-nowrap" },
    cell: ({ row }) => {
      const { oldestDueDate, daysOverdue } = row.original;
      return (
        <div>
          <p className="text-foreground">{oldestDueDate ? formatDate(oldestDueDate) : "—"}</p>
          <p
            className={cn(
              "text-xs",
              daysOverdue > 0 ? "text-danger-text" : "text-muted-foreground"
            )}
          >
            {daysOverdue > 0 ? `${pluralize(daysOverdue, "day")} late` : "Not due yet"}
          </p>
        </div>
      );
    },
  },
];

const csvColumns: CsvColumn<OutstandingClientRow>[] = [
  ...CLIENT_CSV_COLUMNS,
  { header: "Open invoices", value: (row) => row.openInvoices },
  { header: "Outstanding (INR)", value: (row) => csvRupees(row.outstanding) },
  { header: "Overdue (INR)", value: (row) => csvRupees(row.overdue) },
  { header: "Oldest due", value: (row) => row.oldestDueDate },
  { header: "Days late", value: (row) => row.daysOverdue },
  ...AGEING.map((bucket) => ({
    header: `${bucket.label} (INR)`,
    value: (row: OutstandingClientRow) => csvRupees(row.buckets[bucket.key]),
  })),
];

interface OutstandingBodyProps {
  overdueOnly: boolean;
  onOverdueOnlyChange: (overdueOnly: boolean) => void;
}

/** The drawer's content; mounted only while it is open, so nothing loads before that. */
const OutstandingBody: React.FC<OutstandingBodyProps> = ({ overdueOnly, onOverdueOnlyChange }) => {
  const history = useHistory();
  const today = todayIST();
  // Outstanding is always as of today; the period only matters to Collections, so this shares
  // the Collections drawer's default request.
  const range = React.useMemo(() => presetRange("this_month", today), [today]);
  const query = useBillingAnalyticsQuery(range);
  const outstanding = query.data?.outstanding;
  const allClients = outstanding?.clients || [];
  const clients = overdueOnly ? allClients.filter((row) => row.overdue > 0) : allClients;
  const shownTotal = clients.reduce((sum, row) => sum + row.outstanding, 0);
  const shownOverdue = clients.reduce((sum, row) => sum + row.overdue, 0);
  const asOf = query.data?.today || today;

  const exportCsv = () =>
    downloadCsv(clients, csvColumns, `outstanding-${overdueOnly ? "overdue-" : ""}${asOf}.csv`);

  let content: React.ReactNode;
  if (query.isError && !query.data) {
    content = <ReportError error={query.error} onRetry={() => query.refetch()} />;
  } else if (outstanding && outstanding.total === 0) {
    content = (
      <EmptyState
        icon={<CircleCheck />}
        title="Nothing outstanding"
        description="Every issued invoice is paid."
      />
    );
  } else {
    const loading = query.isLoading;
    const overdueClients = allClients.filter((row) => row.overdue > 0).length;
    content = (
      <div className="space-y-6">
        <ReportSummary
          loading={loading}
          figures={[
            {
              label: "Outstanding",
              value: formatINR(outstanding?.total || 0),
              hint: `${pluralize(allClients.length, "client")} owe money`,
            },
            {
              label: "Overdue",
              value: formatINR(outstanding?.overdue || 0),
              hint: `${pluralize(overdueClients, "client")} past due`,
              tone: outstanding && outstanding.overdue > 0 ? "danger" : "default",
            },
            { label: "Not yet due", value: formatINR(outstanding?.notDue || 0) },
          ]}
        />

        <section aria-label="By age" className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">How late</h3>
          {outstanding ? (
            <AgeingBar buckets={outstanding.buckets} total={outstanding.total} />
          ) : (
            <Skeleton className="h-16 w-full" />
          )}
        </section>

        <section aria-label="By client" className="space-y-3">
          <ReportTableHeading
            title="By client"
            detail="Most overdue first. Open a connected client to see its invoices."
            onExport={clients.length > 0 ? exportCsv : undefined}
          >
            <Switch
              label="Overdue only"
              checked={overdueOnly}
              onCheckedChange={onOverdueOnlyChange}
            />
          </ReportTableHeading>
          <DataTable
            columns={columns}
            data={clients}
            loading={loading}
            getRowId={(row) => row.tenantId}
            onRowClick={(row) => history.push(`/billing/${row.tenantId}?tab=invoices`)}
            isRowClickable={(row) => isConnected(row.link)}
            enableSorting={false}
            density="compact"
            caption="Outstanding by client"
            empty={
              <EmptyState
                compact
                icon={<CircleCheck />}
                title="Nothing overdue"
                description="Every client is within its due dates."
                action={
                  <Button size="sm" variant="secondary" onClick={() => onOverdueOnlyChange(false)}>
                    Show everything owed
                  </Button>
                }
              />
            }
            footer={
              clients.length > 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">
                    {pluralize(clients.length, "client")}
                    {shownOverdue > 0 && (
                      <>
                        {" · "}
                        <span className="tabular-nums text-danger-text">
                          {formatINR(shownOverdue)} overdue
                        </span>
                      </>
                    )}
                  </span>
                  <span className="font-semibold tabular-nums text-foreground">
                    Total {formatINR(shownTotal)}
                  </span>
                </div>
              ) : undefined
            }
          />
        </section>
      </div>
    );
  }

  return (
    <>
      <SheetHeader
        title="Outstanding"
        description={`What each client owes on issued invoices as of ${formatDate(
          asOf
        )}, and how late it is. Draft and void invoices don't count.`}
      />
      <SheetBody>{content}</SheetBody>
    </>
  );
};

interface OutstandingSheetProps extends OutstandingBodyProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** What each client owes today and how late (from the Outstanding and Overdue tiles). */
const OutstandingSheet: React.FC<OutstandingSheetProps> = ({ open, onOpenChange, ...props }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent size="xl" onOpenAutoFocus={focusDrawer}>
      <OutstandingBody {...props} />
    </SheetContent>
  </Sheet>
);

export default OutstandingSheet;
